import { ChevronDown } from "lucide-react"
import * as React from "react"

import { type DetectorProfileResolved } from "@/components/domain/detector-profile"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { gapTotalOf, scopeTallyOf, type ReportCoverage } from "../report-data"

import { ReportSettings } from "./report-settings"

/**
 * The report's scope statement: what this review checked, and what it did not.
 *
 * The summary says the printed label was reviewed against its master, and a
 * client reads that as a complete check. It is not one — a requirement with no
 * check behind it raises no finding, exactly like one that passed — so this
 * renders on every report, a clean one most of all. Wording ported from the
 * legacy console's `CoverageBlock`.
 */
export function CoverageBlock({
  coverage,
  detectorProfile = null,
}: {
  coverage: ReportCoverage
  /** The settings the review applied. Null when it recorded none. */
  detectorProfile?: DetectorProfileResolved | null
}): JSX.Element {
  const headingId = React.useId()
  const tally = scopeTallyOf(coverage)

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border border-border bg-surface p-6 shadow-e1"
    >
      <h2 id={headingId} className="text-eyebrow uppercase text-text-3">
        Scope of this review
      </h2>

      {tally === "unrecorded" && (
        <p className="mt-3 text-body text-text-2">
          The scope of this review was not recorded. Absence of a finding below is not confirmation
          that a requirement was met.
        </p>
      )}
      {/* The reason sits in the scope notice above the tabs; this says what it
          means for the count. Some checks still ran — those that never read the
          label's text — so the sentence withholds the number rather than
          claiming nothing was checked. */}
      {tally === "unreadable" && (
        <p className="mt-3 text-body text-text-2">
          No requirement tally is shown for this label: the checks that did run were the ones that
          never rely on its text, and a count beside them would read as a share of a review that did
          not happen.
        </p>
      )}
      {tally === "counts" && <CoverageCounts coverage={coverage} />}
      <ReportSettings profile={detectorProfile} />
    </section>
  )
}

function CoverageCounts({ coverage }: { coverage: ReportCoverage }): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const listId = React.useId()
  const notApplicable = coverage.notApplicableRuleIds
  const unanswered = coverage.unansweredRuleIds
  const gapTotal = gapTotalOf(coverage)
  const hasList =
    coverage.checkedRuleIds.length > 0 ||
    coverage.gaps.length > 0 ||
    notApplicable.length > 0 ||
    unanswered.length > 0

  return (
    <>
      {/* checked + not applicable + unanswered + not examined = answerable.
          "Not examined" is its own category, not "the remaining N": a reader
          subtracts "remaining" from the nearest pair of numbers and gets it
          wrong whenever either middle sentence renders. */}
      <p className="mt-3 text-body text-text-2">
        This review checked{" "}
        <strong className="font-semibold text-text">
          {coverage.checked} of {coverage.answerable}
        </strong>{" "}
        label requirements.{" "}
        {notApplicable.length > 0 && (
          <>
            <strong className="font-semibold text-text">{notApplicable.length}</strong> did not
            apply to this label: a requirement is only checked where its precondition holds.{" "}
          </>
        )}
        {unanswered.length > 0 && (
          <>
            <strong className="font-semibold text-text">{unanswered.length}</strong> could not be
            answered on this run, because a stage of the review did not complete. That is a gap in
            this run rather than anything about the label, and re-running the review is what closes
            it.{" "}
          </>
        )}
        {gapTotal > 0 && (
          <>
            <strong className="font-semibold text-text">{gapTotal}</strong> were{" "}
            <strong className="font-semibold text-text">not examined</strong> — no finding was
            raised against them, and that is not a statement that they are met.
          </>
        )}
      </p>

      {coverage.gaps.length > 0 && (
        <ul
          aria-label="Requirements not examined, by kind"
          className="mt-4 divide-y divide-border-soft overflow-hidden rounded-lg border border-border"
        >
          {coverage.gaps.map((gap) => (
            <li
              key={gap.lane}
              className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-x-4 px-4 py-3 sm:grid-cols-[2.5rem_minmax(0,1fr)_7rem]"
            >
              {/* A right-aligned figure column rather than a chip per row:
                  the counts line up and read as one tally. */}
              <span className="text-right text-heading font-semibold tabular-nums text-text">
                {gap.count}
              </span>
              <span className="text-body-sm text-text-2">{gap.label}</span>
              {/* Each kind's share of what went unexamined, so the large lanes
                  stand out without reading the numbers. Decorative: the count
                  already says it. */}
              <span
                aria-hidden="true"
                className="hidden h-1.5 overflow-hidden rounded-full bg-surface-sunken sm:block"
              >
                <span
                  className="block h-full rounded-full bg-text-3/50"
                  style={{
                    width: `${String(gapTotal > 0 ? Math.max(4, (gap.count / gapTotal) * 100) : 0)}%`,
                  }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Rule ids are provenance, not reading material: collapsed so the block
          stays a paragraph for a reader who only needs its shape. Offered only
          when there is something behind it. The region is always in the DOM and
          hidden when closed, so `aria-controls` never points at nothing. */}
      {hasList && (
        <>
          <Button
            size="sm"
            variant="ghost"
            className="-ml-2 mt-4"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => {
              setOpen((v) => !v)
            }}
          >
            <ChevronDown
              className={cn(
                "size-4 transition-transform duration-120 motion-reduce:transition-none",
                open && "rotate-180",
              )}
              aria-hidden="true"
            />
            {open ? "Hide the requirement list" : "Show the requirement list"}
          </Button>

          <div id={listId} hidden={!open} className="mt-3 space-y-4">
            <RuleGroup title="Checked" ruleIds={coverage.checkedRuleIds} />
            {/* Between the checked and the unchecked: asked and answered, the
                answer being that they do not govern this label. */}
            <RuleGroup title="Did not apply to this label" ruleIds={notApplicable} />
            {/* Asked, unlike a gap, and unanswered, unlike either list above:
                named for the run rather than the label. */}
            <RuleGroup title="Not answered on this run" ruleIds={unanswered} />
            {coverage.gaps.map((gap) => (
              <RuleGroup
                key={gap.lane}
                title={`Not checked — ${gap.label}`}
                ruleIds={gap.ruleIds}
              />
            ))}
          </div>
        </>
      )}
    </>
  )
}

function RuleGroup({
  title,
  ruleIds,
}: {
  title: string
  ruleIds: readonly string[]
}): JSX.Element | null {
  const headingId = React.useId()
  if (ruleIds.length === 0) return null
  return (
    <div>
      <h3 id={headingId} className="text-eyebrow uppercase text-text-3">
        {title}
      </h3>
      <ul aria-labelledby={headingId} className="mt-1.5 flex flex-wrap gap-1.5">
        {ruleIds.map((id) => (
          <li
            key={id}
            className="rounded-sm border border-border-soft bg-surface-sunken px-1.5 py-0.5 font-id text-caption text-text-2"
          >
            {id}
          </li>
        ))}
      </ul>
    </div>
  )
}
